const buildStandings = (players, meId) => {
  const sorted = [...players].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  const standings = [];
  let rank = 0;
  let lastScore = null;
  sorted.forEach((p, i) => {
    if (p.score !== lastScore) {
      rank = i + 1;
      lastScore = p.score;
    }
    standings.push({
      id: p.id,
      rank,
      name: p.name,
      score: p.score ?? 0,
      isMe: p.id === meId,
    });
  });
  return standings;
};

const ordinalLabel = (rank, isTied) => {
  const ordinals = { 1: "1ST PLACE", 2: "2ND PLACE", 3: "3RD PLACE" };
  const base = ordinals[rank] || `${rank}TH PLACE`;
  return isTied ? `T-${base}` : base;
};

const playerWonSuperlative = (player, sup) => {
  if (!sup?.winner) return false;
  // winner string can be "Marcus" or "Marcus, Sarah" for ties
  return sup.winner
    .split(",")
    .map((n) => n.trim().toLowerCase())
    .includes(player.name.trim().toLowerCase());
};

const SUPERLATIVE_PRIORITY = [
  "Trivia Wiz",
  "Come From Behind",
  "Comeback Kid",
  "Fastest Trigger",
  "Daily Double Daredevil",
  "In Control",
  "Hottest Streak",
  "Buzzer Beater",
  "Big Guesser",
  "Over Confident",
  "Most Risk Averse",
];

const RECAP_HIGHLIGHT_ORDER = [
  "Trivia Wiz",
  "Fastest Trigger",
  "Hottest Streak",
  "Easiest Category",
  "Hardest Category",
];

const buildRecapHighlights = (superlatives) => {
  const byHeader = new Map();
  for (const s of superlatives) byHeader.set(s.header, s);
  const ordered = [];
  for (const h of RECAP_HIGHLIGHT_ORDER) {
    const s = byHeader.get(h);
    if (s) ordered.push({ id: s.id, header: s.header, winner: s.winner });
  }
  if (ordered.length < 5) {
    for (const s of superlatives) {
      if (ordered.length >= 5) break;
      if (RECAP_HIGHLIGHT_ORDER.includes(s.header)) continue;
      ordered.push({ id: s.id, header: s.header, winner: s.winner });
    }
  }
  return ordered.slice(0, 5);
};

const pickPlayerSuperlatives = (superlatives, player) => {
  const winsByPlayer = superlatives.filter((s) => playerWonSuperlative(player, s));
  return winsByPlayer
    .slice()
    .sort((a, b) => {
      const ai = SUPERLATIVE_PRIORITY.indexOf(a.header);
      const bi = SUPERLATIVE_PRIORITY.indexOf(b.header);
      const aRank = ai === -1 ? 999 : ai;
      const bRank = bi === -1 ? 999 : bi;
      return aRank - bRank;
    })
    .slice(0, 3);
};

export const buildShareCards = ({ player, players, superlatives }) => {
  const sortedPlayers = [...players].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  const standings = buildStandings(players, player.id);
  const myStanding = standings.find((s) => s.id === player.id);
  const isTied = myStanding
    ? standings.filter((s) => s.rank === myStanding.rank).length > 1
    : false;
  const rankLabel = myStanding
    ? ordinalLabel(myStanding.rank, isTied)
    : ordinalLabel(sortedPlayers.length, false);

  const cards = [];

  // 1. Rank + leaderboard
  // correctCount lives on player.shareStats (written by the backend
  // superlatives function). Reading player.correctCount directly was
  // always undefined → "0 correct" on every rank card.
  cards.push({
    variant: "rank",
    data: {
      rankLabel,
      playerName: player.name,
      score: player.score ?? 0,
      correctCount: player.shareStats?.correctCount ?? 0,
      standings,
    },
  });

  if (player.shareStats) {
    cards.push({
      variant: "stats",
      data: {
        playerName: player.shareStats.playerName,
        score: player.shareStats.score,
        correctCount: player.shareStats.correctCount,
        fastestText: player.shareStats.fastestText,
        accuracyText: player.shareStats.accuracyText,
        firstBuzzes: player.shareStats.firstBuzzes,
        bestCategory: player.shareStats.bestCategory,
      },
    });
  }

  // 2. One per superlative the player won (capped at 3, priority-sorted)
  const playerSupes = pickPlayerSuperlatives(superlatives, player);
  for (const sup of playerSupes) {
    cards.push({
      variant: "superlative",
      data: {
        superlativeId: sup.id,
        header: sup.header,
        winner: sup.winner,
        subhead: sup.subhead,
        winnerDetail: sup.winnerDetail,
        emoji: sup.emoji,
      },
    });
  }

  const finalCard = player.shareStats?.finalCard;
  if (finalCard) {
    cards.push({
      variant: "finalClue",
      data: {
        category: finalCard.category,
        clueText: finalCard.clueText,
        wager: finalCard.wager,
        playerName: player.name,
        finalScore: player.score ?? 0,
      },
    });
    cards.push({
      variant: "finalAnswer",
      data: {
        category: finalCard.category,
        answer: finalCard.answer,
        wager: finalCard.wager,
        playerName: player.name,
        finalScore: player.score ?? 0,
      },
    });
  }

  // 3. Full game recap
  const winner = sortedPlayers[0];
  const highlights = buildRecapHighlights(superlatives);
  cards.push({
    variant: "recap",
    data: {
      highlights,
      winnerName: winner?.name ?? "—",
      winnerScore: winner?.score ?? 0,
    },
  });

  return cards;
};
